import React, { useState, useEffect, useRef } from "react";
import {
	motion,
	useViewportScroll,
	useTransform,
	AnimatePresence,
} from "framer-motion";

import { Heading } from "@/components/Typography";
import PageShare from "@/components/WineDetail/PageShare/PageShare";
import Specifications from "@/components/WineDetail/Specifications/Specifications";
import HtmlParser from "@/components/HtmlParser";
import { Wine } from "@/models/wine";
import { useImage } from "@/api/hooks";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { useMatchMedia } from "@/hooks/use-match-media";

import { IntroIllustration } from "./IntroIllustration";
import { IntroBottle } from "./IntroBottle";

import s from "./Intro.module.scss";

interface Props {
	wine: Wine;
}

export function Intro({ wine }: Props) {
	const [featuredImage, bottleLoading, botleError] = useImage(
		wine?.featured_media
	);
	const [shouldAnimate, setShouldAnimate] = useState(false);
	const isBreakpointLG = useBreakpoint("lg");
	const isBreakpointMD = useBreakpoint("md");
	const isPortraitScreen = useMatchMedia("(orientation: portrait)");
	const { scrollY } = useViewportScroll();
	const bottleRef = useRef<HTMLDivElement | null>(null);

	const shouldTransform = useTransform(scrollY, (value) => {
		if (isBreakpointLG) {
			return value >= 500;
		} else if (isBreakpointMD) {
			if (isPortraitScreen) return value >= 600;
			return value >= 360;
		}
		return value >= 300;
	});

	useEffect(() => {
		const unsubscribe = shouldTransform.onChange((latest) => {
			const callback = () => {
				if (latest !== shouldAnimate) {
					setShouldAnimate(latest);
				}
			};

			callback();
		});

		return () => {
			unsubscribe();
		};
	}, [shouldAnimate, shouldTransform]);

	return (
		<AnimatePresence>
			<section className={s.intro}>
				<Heading as="h1" sizeMobile="lg" sizeDesktop="xxl" className={s.leader}>
					<HtmlParser content={wine.acf.leader} />
				</Heading>
				<div className={s.animation_wrapper}>
					<div className={s.background__wrapper}>
						<div className={s.background}>
							<IntroIllustration tasteProfile={wine.acf.taste_profile.name} />
						</div>

						{!shouldAnimate && (
							<motion.div
								layoutId="bottle"
								initial="huge"
								style={{
									margin: "0 auto",
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									width: isBreakpointMD ? 260 : 162,
								}}
							>
								{featuredImage && !bottleLoading && !botleError && (
									<IntroBottle image={featuredImage} />
								)}
							</motion.div>
						)}
					</div>

					<div className={s.info}>
						<Specifications specs={wine.acf} />

						<div className={s.infoWineContainer} ref={bottleRef}>
							{shouldAnimate && (
								<motion.div className={s.bottle} layoutId="bottle">
									{featuredImage && !bottleLoading && !botleError && (
										<IntroBottle image={featuredImage} />
									)}
								</motion.div>
							)}
						</div>

						<div className={s.right__wrapper}>
							<div className={s.rightWrapper__rightInfo}>
								<Heading
									className={s.small_heading}
									as="p"
									sizeMobile="xxs"
									sizeDesktop="xxs"
								>
									Naam
								</Heading>
								<Heading
									as="h2"
									sizeMobile="md"
									sizeDesktop="xl"
									itemProp="name"
								>
									{wine.name}
								</Heading>
								{wine.acf.producer && (
									<>
										<Heading
											className={s.small_heading}
											as="p"
											sizeMobile="xxs"
											sizeDesktop="xxs"
										>
											Producent
										</Heading>
										<Heading
											as="h3"
											sizeMobile="sm"
											sizeDesktop="sm"
											itemProp="name"
											className={s.producer}
										>
											{wine.acf.producer}
										</Heading>
									</>
								)}
								<PageShare
									wine={wine}
									label="Deel deze wijn"
									productImage={featuredImage}
								></PageShare>
							</div>
						</div>
					</div>
				</div>
			</section>
		</AnimatePresence>
	);
}
